import { format, isAfter, isBefore, parse } from "date-fns";

type DateValidationProps = {
  dateValue: string;
  minDate: Date;
  maxDate: Date;
  fieldType: "start" | "end";
};

/**
 * @description Validation function that returns an error message if the date value is outside of min-max date range
 * @param dateValue - The date value to be validated
 * @param minDate - The minimum allowable date
 * @param maxDate - The maximum allowable date
 * @param fieldType - "start" or "end" to determine error messaging
 * @returns string, error message or empty string
 */
export const validateDate = ({
  dateValue,
  minDate,
  maxDate,
  fieldType,
}: DateValidationProps) => {
  if (!dateValue) return "";

  const date = parse(dateValue, "yyyy-MM-dd", new Date());
  const fieldLabel = fieldType === "start" ? "Start date" : "End date";

  if (isBefore(date, minDate) || isAfter(date, maxDate)) {
    const limitDate = isBefore(date, minDate) ? minDate : maxDate;
    const comparison = isBefore(date, minDate) ? "earlier" : "later";
    return `${fieldLabel} cannot be ${comparison} than ${format(limitDate, "dd/MM/yyyy")}.`;
  }

  return "";
};

/**
 * @description Validates the range of dates and returns an error message if end date of the range is before the start date
 * @param startDateValue
 * @param endDateValue
 * @returns string, Error message - if the end date is before the start date or empty string
 */
export const validateDateRange = (
  startDateValue: string,
  endDateValue: string,
) => {
  if (!startDateValue || !endDateValue) return "";

  const start = new Date(startDateValue);
  const end = new Date(endDateValue);

  if (isBefore(end, start)) {
    return "End date must be after start date.";
  }

  return "";
};
